import { useState, useCallback, useEffect } from 'react';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  Patient,
  Specimen,
  ServiceRequest,
  CodeableConcept,
  Media,
} from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useTranslation } from 'react-i18next';
import {
  ARKON,
  SYSTEMS,
  EXTERNAL_CS,
  IDENTIFIERS,
} from '@consts/systems.consts';
import { generateSampleTypeCode } from '../utils/code-generation-util';
import { isValidSampleTypeCode } from '../utils/specimen-validation';
import { resolveId } from '@medplum/core';
import { PhotoFile } from '@/pages/ainigma/patient/examination/photos/PhotoInput';
import {
  loadSpecimenPhotos,
  saveSpecimenPhotos,
} from '../utils/specimen-photo.helpers';
import { SPECIMEN_STATUS } from '../constants/specimen.consts';

export interface SpecimenFormData {
  type: CodeableConcept | undefined;
  typeDisplay?: string;
  collectionDate: Date | null;
  registrationDate: Date | null;
  qualityGood: boolean;
  qualityReason?: string;
  photos: PhotoFile[];
  notes?: string;
  status: string;
  externalId?: string;
  selectedPatient?: Patient;
  selectedOrders: ServiceRequest[];
}

interface UseSpecimenFormProps {
  specimen?: Specimen | null;
  patientId?: string;
  defaultSampleType?: CodeableConcept;
  opened: boolean;
  onClose: () => void;
  serviceRequest?: ServiceRequest;
}

export function useSpecimenForm({
  specimen,
  patientId,
  defaultSampleType,
  opened,
  onClose,
  serviceRequest,
}: UseSpecimenFormProps) {
  const medplum = useMedplum();
  const { t } = useTranslation(['lims', 'common']);
  const [loading, setLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | undefined>();
  const [selectedOrders, setSelectedOrders] = useState<ServiceRequest[]>([]);
  const [availableOrders, setAvailableOrders] = useState<ServiceRequest[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [originalLinkedOrders, setOriginalLinkedOrders] = useState<
    ServiceRequest[]
  >([]);
  const [existingMedia, setExistingMedia] = useState<Media[]>([]);

  const form = useForm<SpecimenFormData>({
    initialValues: {
      type: undefined,
      typeDisplay: '',
      collectionDate: new Date(),
      registrationDate: new Date(),
      qualityGood: true,
      qualityReason: '',
      photos: [],
      notes: '',
      status: SPECIMEN_STATUS.AVAILABLE,
      externalId: '',
      selectedPatient: undefined,
      selectedOrders: [],
    },
    validate: {
      type: (value) => (!value ? t('validation.specimenTypeRequired') : null),
      collectionDate: (value) =>
        !value ? t('validation.collectionDateRequired') : null,
      registrationDate: (value) =>
        !value ? t('validation.registrationDateRequired') : null,
      selectedPatient: (value) =>
        !patientId && !value ? t('validation.patientSelectionRequired') : null,
    },
  });

  // Load available orders for the patient
  const loadAvailableOrders = useCallback(
    async (patientReference: string, specimenType?: CodeableConcept) => {
      if (!patientReference) {
        setAvailableOrders([]);
        return;
      }

      if (!specimenType) {
        setAvailableOrders([]);
        return;
      }

      setLoadingOrders(true);
      try {
        const orders = await medplum.searchResources('ServiceRequest', {
          subject: patientReference,
          category: `${SYSTEMS.SNOMED}|${EXTERNAL_CS.SNOMED.CODES.LABORATORY_PROCEDURE}`,
          status: 'active,draft',
          _sort: '-authored',
          _count: '50',
        });

        let filteredOrders = [...orders];
        if (specimenType) {
          filteredOrders = orders.filter((order) => {
            const sampleTypeExt = order.extension?.find(
              (ext) =>
                ext.url === ARKON.CS.SPECIMEN.TYPE.BASE ||
                ext.url === ARKON.SD.SPECIMEN_TYPE
            );

            const orderSampleType = sampleTypeExt?.valueCodeableConcept;

            return (
              orderSampleType?.coding?.[0].code ===
              specimenType?.coding?.[0].code
            );
          });
        }

        setAvailableOrders(filteredOrders);
      } catch (error) {
        console.error('Error loading available orders:', error);
        setAvailableOrders([]);
      } finally {
        setLoadingOrders(false);
      }
    },
    [medplum]
  );

  // Load orders when patient or specimen type changes
  useEffect(() => {
    const patientReference = patientId
      ? `Patient/${patientId}`
      : selectedPatient?.id
        ? `Patient/${selectedPatient.id}`
        : specimen?.subject?.reference;

    if (opened && patientReference) {
      const specimenType = specimen?.type || defaultSampleType;
      loadAvailableOrders(patientReference, specimenType);
    } else {
      setAvailableOrders([]);
    }
  }, [
    opened,
    patientId,
    selectedPatient?.id,
    specimen?.subject?.reference,
    specimen?.type,
    defaultSampleType,
    loadAvailableOrders,
  ]);

  // Load linked orders for editing an existing specimen
  useEffect(() => {
    if (opened && specimen?.request?.length) {
      const loadLinkedOrders = async () => {
        try {
          const orders: ServiceRequest[] = [];
          for (const requestRef of specimen.request || []) {
            const orderId = resolveId(requestRef);
            if (!orderId) {
              continue;
            }
            try {
              const order = await medplum.readResource(
                'ServiceRequest',
                orderId
              );
              orders.push(order);
            } catch (error) {
              console.error(`Failed to load order ${orderId}:`, error);
            }
          }
          setSelectedOrders(orders);
          setOriginalLinkedOrders(orders);
          form.setFieldValue('selectedOrders', orders);
        } catch (error) {
          console.error('Error loading linked orders:', error);
        }
      };
      loadLinkedOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specimen?.id, specimen?.request, opened]);

  // Populate form when editing an existing specimen
  useEffect(() => {
    if (specimen && opened) {
      const conditionCode = specimen.condition?.[0]?.coding?.[0]?.code;
      const qualityGood =
        conditionCode === ARKON.CS.SPECIMEN.CONDITION.CODES.SATISFACTORY;
      const qualityReason = specimen.condition?.[0]?.text || '';

      const externalId = specimen.identifier?.find(
        (id) => id.system === IDENTIFIERS.SPECIMEN_EXTERNAL_ID
      )?.value;

      const formValues = {
        type: specimen?.type || undefined,
        typeDisplay: specimen.type?.coding?.[0]?.display || '',
        collectionDate: specimen.collection?.collectedDateTime
          ? new Date(specimen.collection.collectedDateTime)
          : null,
        registrationDate: specimen.receivedTime
          ? new Date(specimen.receivedTime)
          : null,
        qualityGood,
        qualityReason,
        photos: [] as PhotoFile[],
        notes: specimen.note?.[0]?.text || '',
        status: (specimen.status as any) || SPECIMEN_STATUS.AVAILABLE,
        externalId: externalId || '',
        selectedPatient: undefined,
        selectedOrders: [],
      };

      form.setValues(formValues);
      form.resetDirty(formValues);

      // Load existing photos for this specimen
      loadSpecimenPhotos(medplum, specimen).then(
        ({ photos, existingMedia: media }) => {
          setExistingMedia(media);
          form.setFieldValue('photos', photos);
        }
      );
    } else if (!specimen && opened) {
      const resetValues = {
        type: defaultSampleType || undefined,
        typeDisplay: '',
        collectionDate: new Date(),
        registrationDate: new Date(),
        qualityGood: true,
        qualityReason: '',
        photos: [] as PhotoFile[],
        notes: '',
        status: SPECIMEN_STATUS.AVAILABLE,
        externalId: '',
        selectedPatient: undefined,
        selectedOrders: [],
      };
      form.setValues(resetValues);
      form.resetDirty(resetValues);
      setSelectedPatient(undefined);
      setSelectedOrders([]);
      setOriginalLinkedOrders([]);
      setExistingMedia([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specimen?.id, opened, defaultSampleType]);

  useEffect(() => {
    if (!opened || specimen || !defaultSampleType) {
      return;
    }

    if (!form.values.type) {
      form.setFieldValue('type', defaultSampleType);
    }
  }, [defaultSampleType, form, opened, specimen]);

  // Auto-select the serviceRequest when creating a new specimen from order context
  useEffect(() => {
    if (opened && !specimen && serviceRequest?.id) {
      setSelectedOrders([serviceRequest]);
      form.setFieldValue('selectedOrders', [serviceRequest]);
    }
  }, [opened, specimen, serviceRequest, form]);

  const handleSubmit = useCallback(
    async (values: SpecimenFormData) => {
      const patientReference = patientId
        ? `Patient/${patientId}`
        : selectedPatient?.id
          ? `Patient/${selectedPatient.id}`
          : specimen?.subject?.reference;

      if (!patientReference) {
        notifications.show({
          title: 'Error',
          message: t('notifications.specimen.patientRequired'),
          color: 'red',
        });
        return;
      }

      // Trim whitespace from external ID
      const trimmedExternalId = values.externalId?.trim() || '';

      setLoading(true);

      const sampleId =
        specimen?.identifier?.[0]?.value ||
        generateSampleTypeCode(values.type?.coding?.[0].code || '');

      // Handle invalid sample type (null returned from generateSampleTypeCode)
      if (!sampleId) {
        notifications.show({
          title: 'Error',
          message: t('notifications.specimen.invalidSampleType'),
          color: 'red',
        });
        setLoading(false);
        return;
      }

      // Validation
      if (!isValidSampleTypeCode(sampleId)) {
        notifications.show({
          title: 'Error',
          message:
            'Invalid specimen identifier format generated. Please try again.',
          color: 'red',
        });
        setLoading(false);
        return;
      }

      try {
        const conditionCode = values.qualityGood
          ? ARKON.CS.SPECIMEN.CONDITION.CODES.SATISFACTORY
          : ARKON.CS.SPECIMEN.CONDITION.CODES.UNSATISFACTORY;
        const conditionDisplay = values.qualityGood
          ? 'Satisfactory'
          : 'Unsatisfactory';

        const specimenData: Specimen = {
          resourceType: 'Specimen',
          ...(specimen && { id: specimen.id }),
          identifier: [
            {
              value: sampleId,
              system: ARKON.CS.SPECIMEN.BARCODE.BASE,
            },
            ...(trimmedExternalId
              ? [
                  {
                    value: trimmedExternalId,
                    system: IDENTIFIERS.SPECIMEN_EXTERNAL_ID,
                  },
                ]
              : []),
          ],
          status: values.status as any,
          type: values.type,
          subject: {
            reference: patientReference,
          },
          receivedTime: values.registrationDate
            ? values.registrationDate.toISOString()
            : undefined,
          collection: {
            collectedDateTime: values.collectionDate
              ? values.collectionDate.toISOString()
              : new Date().toISOString(),
          },
          condition: [
            {
              coding: [
                {
                  system: ARKON.CS.SPECIMEN.CONDITION.BASE,
                  code: conditionCode,
                  display: conditionDisplay,
                },
              ],
              ...(!values.qualityGood && values.qualityReason?.trim()
                ? { text: values.qualityReason.trim() }
                : {}),
            },
          ],
          ...(selectedOrders.length > 0 && {
            request: selectedOrders.map((order) => ({
              reference: `ServiceRequest/${order.id}`,
            })),
          }),
          ...(values.notes && {
            note: [
              {
                text: values.notes,
              },
            ],
          }),
        };

        let createdOrUpdatedSpecimen: Specimen;

        if (specimen) {
          createdOrUpdatedSpecimen = await medplum.updateResource(specimenData);
          notifications.show({
            title: t('success', { ns: 'common' }),
            message: t('notifications.specimen.updateSuccess'),
            color: 'green',
          });
        } else {
          createdOrUpdatedSpecimen = await medplum.createResource(specimenData);
          notifications.show({
            title: t('success', { ns: 'common' }),
            message: t('notifications.specimen.createSuccess'),
            color: 'green',
          });
        }

        // Handle photo upload/deletion using the helper
        if (createdOrUpdatedSpecimen.id) {
          try {
            await saveSpecimenPhotos(
              medplum,
              createdOrUpdatedSpecimen,
              values.photos,
              existingMedia,
              patientReference
            );
          } catch (error) {
            console.error('Error saving specimen photos:', error);
          }
        }

        // Handle order linking/unlinking for editing existing specimen
        if (specimen && createdOrUpdatedSpecimen.id) {
          // Find orders that were removed
          const removedOrders = originalLinkedOrders.filter(
            (originalOrder) =>
              !selectedOrders.some(
                (selected) => selected.id === originalOrder.id
              )
          );

          // Remove specimen reference from orders that were unlinked
          for (const order of removedOrders) {
            try {
              const currentSpecimens = order.specimen || [];
              const specimenRef = `Specimen/${createdOrUpdatedSpecimen.id}`;

              const updatedSpecimens = currentSpecimens.filter(
                (s: any) => s.reference !== specimenRef
              );

              if (updatedSpecimens.length !== currentSpecimens.length) {
                await medplum.patchResource('ServiceRequest', order.id || '', [
                  {
                    op: updatedSpecimens.length === 0 ? 'remove' : 'replace',
                    path: '/specimen',
                    ...(updatedSpecimens.length > 0 && {
                      value: updatedSpecimens,
                    }),
                  },
                ]);
              }
            } catch (error) {
              console.error(
                `Failed to update ServiceRequest ${order.id}:`,
                error
              );
            }
          }

          // Add specimen reference to newly linked orders
          const newlyLinkedOrders = selectedOrders.filter(
            (selectedOrder) =>
              !originalLinkedOrders.some(
                (original) => original.id === selectedOrder.id
              )
          );

          for (const order of newlyLinkedOrders) {
            try {
              const currentSpecimens = order.specimen || [];
              const specimenRef = `Specimen/${createdOrUpdatedSpecimen.id}`;

              if (
                !currentSpecimens.some((s: any) => s.reference === specimenRef)
              ) {
                const updatedSpecimens = [
                  ...currentSpecimens,
                  {
                    reference: specimenRef,
                    display:
                      createdOrUpdatedSpecimen.identifier?.[0]?.value ||
                      createdOrUpdatedSpecimen.id,
                  },
                ];

                await medplum.patchResource('ServiceRequest', order.id || '', [
                  {
                    op: currentSpecimens.length === 0 ? 'add' : 'replace',
                    path: '/specimen',
                    value: updatedSpecimens,
                  },
                ]);
              }
            } catch (error) {
              console.error(
                `Failed to update ServiceRequest ${order.id}:`,
                error
              );
            }
          }
        }
        // For new specimens, link to all selected orders
        else if (
          !specimen &&
          selectedOrders.length > 0 &&
          createdOrUpdatedSpecimen.id
        ) {
          for (const order of selectedOrders) {
            try {
              const currentSpecimens = order.specimen || [];
              const specimenRef = `Specimen/${createdOrUpdatedSpecimen.id}`;

              if (
                !currentSpecimens.some((s: any) => s.reference === specimenRef)
              ) {
                const updatedSpecimens = [
                  ...currentSpecimens,
                  {
                    reference: specimenRef,
                    display:
                      createdOrUpdatedSpecimen.identifier?.[0]?.value ||
                      createdOrUpdatedSpecimen.id,
                  },
                ];

                await medplum.patchResource('ServiceRequest', order.id || '', [
                  {
                    op: currentSpecimens.length === 0 ? 'add' : 'replace',
                    path: '/specimen',
                    value: updatedSpecimens,
                  },
                ]);
              }
            } catch (error) {
              console.error(
                `Failed to update ServiceRequest ${order.id}:`,
                error
              );
            }
          }
        }

        onClose();
      } catch (error: any) {
        console.error('Error saving specimen:', error);
        notifications.show({
          title: 'Error',
          message: specimen
            ? t('notifications.specimen.updateError')
            : t('notifications.specimen.createError'),
          color: 'red',
        });
      } finally {
        setLoading(false);
      }
    },
    [
      medplum,
      specimen,
      patientId,
      selectedPatient,
      selectedOrders,
      originalLinkedOrders,
      existingMedia,
      onClose,
      t,
    ]
  );

  const handlePatientChange = useCallback(
    (patient: Patient | undefined) => {
      setSelectedPatient(patient);
      form.setFieldValue('selectedPatient', patient);
      setSelectedOrders([]);
      form.setFieldValue('selectedOrders', []);
    },
    [form]
  );

  const handleOrdersChange = useCallback(
    (orderIds: string[]) => {
      const newSelectedOrders = availableOrders.filter((order) =>
        orderIds.includes(order.id || '')
      );
      setSelectedOrders(newSelectedOrders);
      form.setFieldValue('selectedOrders', newSelectedOrders);
    },
    [availableOrders, form]
  );

  return {
    // Form state
    form,
    loading,

    // Patient state
    selectedPatient,

    // Orders state
    selectedOrders,
    availableOrders,
    loadingOrders,

    // Handlers
    handleSubmit,
    handlePatientChange,
    handleOrdersChange,
  };
}
